import React, { memo, useRef, useState } from "react";
import {
  Modal,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  FlatList,
} from "react-native";
import ChevronRight from "@assets/svg/ChevronRight";
import FiltersIcon from "@assets/svg/Wallet/Filter";
import Button from "@components/Button";
import Text from "@components/CustomText";
import DatePicker from "@components/DatePicker";
import colors from "@constants/color";
import { FilterType } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import { actions as walletActions } from "@reducers/walletReducer";
import { format } from "date-fns";
import t from "@constants/localization";

type WalletFiltersProps = {
  onApplyFilter?: () => void;
  filterType?: FilterType;
};
const PATH = "screens/Wallet/v2/Components/Filter";
const WalletFilters: React.FC<WalletFiltersProps> = memo(
  ({ onApplyFilter = () => {}, filterType = FilterType.DATEONLY }) => {
    const [shouldFiltersVisible, setShouldFiltersVisible] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("timeDuration");
    const {
      timeFilters,
      transactionFilters,
      currentTimeFilter: ctf,
      currentTransactionFilter: cttf,
    } = useSelector((state) => state.walletReducer);
    const [currentTimeFilter, setCurrentTimeFilter] = useState(ctf || {});
    const [currentTransactionFilter, setCurrentTransactionFilter] = useState(
      cttf || {},
    );
    const dateRef = useRef(null);
    const dispatch = useDispatch();
    const toggleFilters = () => {
      setShouldFiltersVisible((prev) => !prev);
    };

    const categories = [
      { key: "timeDuration", label: "timeAndDuration" },
      { key: "transaction", label: "transaction" },
    ];

    const getCurrentFilters = () => {
      if (selectedCategory === "timeDuration") {
        return timeFilters;
      } else {
        return transactionFilters;
      }
    };

    const handleOptionPress = (item: any) => {
      if (selectedCategory === "timeDuration") {
        setCurrentTimeFilter(item);
      } else if (selectedCategory === "transaction") {
        setCurrentTransactionFilter(item);
      }
    };

    const handleApplyFilter = () => {
      dispatch(walletActions.setTimeFilter(currentTimeFilter));
      dispatch(walletActions.setTransactionFilter(currentTransactionFilter));
      onApplyFilter();
      setShouldFiltersVisible(false);
    };
    function handleDateChange(dateType, date) {
      const today = new Date();
      const { fromDate = today, toDate = today } = currentTimeFilter;
      setCurrentTimeFilter({
        ...currentTimeFilter,
        fromDate: dateType.type === "fromDate" ? date : fromDate,
        toDate: dateType.type === "toDate" ? date : toDate,
      });
    }

    const clearFilters = () => {
      setCurrentTimeFilter({});
      setCurrentTransactionFilter({});
      dispatch(walletActions.resetFilters());
      setShouldFiltersVisible(false);
      onApplyFilter();
    };

    const formatDate = (date: string) => {
      const parsedDate = new Date(date);
      return format(parsedDate, "dd/MM");
    };
    const handleFilterTextPress = (type: string) => {
      if (type === "timeDuration") {
        setShouldFiltersVisible(true);
        setSelectedCategory("timeDuration");
      }
      if (type === "transaction") {
        setShouldFiltersVisible(true);
        setSelectedCategory("transaction");
      }
    };
    const renderCategory = ({
      item,
    }: {
      item: { key: string; label: string };
    }) => (
      <TouchableOpacity
        style={[
          styles.categoryButton,
          selectedCategory === item.key && styles.activeCategory,
        ]}
        onPress={() => setSelectedCategory(item.key)}
        testID={`${item.key}+${item.label}`}
      >
        <Text
          variant="body_text_3"
          style={selectedCategory === item.key ? styles.activeCategoryText : {}}
        >
          {t(`${PATH}/${item.label}`)}
        </Text>
      </TouchableOpacity>
    );

    const renderOption = ({ item }: { item: any }) => (
      <View>
        <TouchableOpacity
          style={[
            styles.optionButton,
            (currentTimeFilter.key === item.key ||
              currentTransactionFilter.key === item.key) &&
              styles.activeOption,
          ]}
          onPress={() => handleOptionPress(item)}
          disabled={!!item.options}
        >
          <View
            style={[
              styles.outerCircle,
              item.options && { backgroundColor: "rgba(83, 83, 83, 1)" },
              (currentTimeFilter.key === item.key ||
                currentTransactionFilter.key === item.key) &&
                styles.selectedOuterCircle,
            ]}
          >
            {(currentTimeFilter.key === item.key ||
              currentTransactionFilter.key === item.key) && (
              <View style={styles.innerCircle} />
            )}
          </View>
          <View style={{ width: "85%" }}>
            <Text
              variant="body_text_3"
              style={
                currentTimeFilter.key === item.key ||
                currentTransactionFilter.key === item.key
                  ? styles.activeOptionText
                  : {}
              }
            >
              {item.label}
            </Text>
          </View>
        </TouchableOpacity>
        {item.options && (
          <FlatList
            data={item.options}
            renderItem={renderOption}
            keyExtractor={(subItem) => subItem.key}
            style={styles.subOptionList}
          />
        )}
      </View>
    );

    return (
      <>
        <View style={styles.filterContainer}>
          <TouchableOpacity style={styles.beLikeRow} onPress={toggleFilters}>
            <FiltersIcon />
            <Text variant="caption_2" style={styles.filterText}>
              {t(`${PATH}/filter`)}
            </Text>
          </TouchableOpacity>
          {ctf?.label && (
            <TouchableOpacity
              style={styles.beLikeRow}
              onPress={() => handleFilterTextPress("timeDuration")}
            >
              {ctf.key === "CUSTOM" ? (
                <Text variant="caption_2" style={styles.filterText}>
                  {`${formatDate(ctf.fromDate)} - ${formatDate(ctf.toDate)}`}
                </Text>
              ) : (
                <Text variant="caption_2" style={styles.filterText}>
                  {ctf.key === "TODAY" ? t(`${PATH}/today`) : ctf.label}
                </Text>
              )}
            </TouchableOpacity>
          )}
          {cttf?.label && filterType !== FilterType.DATEONLY && (
            <TouchableOpacity
              style={styles.beLikeRow}
              onPress={() => handleFilterTextPress("transaction")}
            >
              <Text variant="caption_2" style={styles.filterText}>
                {cttf.label}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        <Modal
          statusBarTranslucent
          navigationBarTranslucent
          transparent
          visible={shouldFiltersVisible}
          onRequestClose={toggleFilters}
          animationType="fade"
        >
          <TouchableWithoutFeedback onPress={toggleFilters}>
            <View style={styles.modalOverlay} />
          </TouchableWithoutFeedback>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <TouchableOpacity onPress={toggleFilters}>
                  <ChevronRight
                    pathFill={colors.mediumBlack}
                    style={styles.chevronIcon}
                  />
                </TouchableOpacity>
                <Text variant="title_18" fontType="head3">
                  {t(`${PATH}/filters`)}
                </Text>
              </View>
              <TouchableOpacity onPress={clearFilters}>
                <Text variant="body_text_1" style={styles.clearText}>
                  {t(`${PATH}/clearAll`)}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.scrollContainer}>
              {filterType !== FilterType.DATEONLY && (
                <FlatList
                  data={categories}
                  renderItem={renderCategory}
                  keyExtractor={(item) => item.key}
                  style={styles.categoryList}
                />
              )}
              <FlatList
                data={getCurrentFilters()}
                renderItem={renderOption}
                keyExtractor={(item) => item.key}
                style={styles.optionList}
              />
            </View>

            {currentTimeFilter.key === "CUSTOM" &&
              selectedCategory === "timeDuration" && (
                <View style={styles.datePickerContainer}>
                  <DatePicker
                    ref={dateRef}
                    dates={
                      currentTimeFilter?.toDate || currentTimeFilter?.fromDate
                        ? {
                            toDate: currentTimeFilter.toDate,
                            fromDate: currentTimeFilter.fromDate,
                          }
                        : {}
                    }
                    handleDateChanged={handleDateChange}
                  />
                </View>
              )}

            <View style={styles.applyButtonContainer}>
              <View style={styles.buttonWrapper}>
                <Button
                  borderRadius={10}
                  btnText={t(`${PATH}/close`)}
                  buttonWidth="100%"
                  height={42}
                  shouldBoldText
                  buttonFontType="head3"
                  buttonColor={colors.white}
                  textColor={colors.black}
                  borderColor={colors.black}
                  onPress={toggleFilters}
                />
              </View>
              <View style={[styles.buttonWrapper, styles.applyButtonWrapper]}>
                <Button
                  borderRadius={10}
                  btnText={t(`${PATH}/apply`)}
                  buttonWidth="100%"
                  height={42}
                  shouldBoldText
                  buttonFontType="head3"
                  buttonColor={colors.black}
                  onPress={handleApplyFilter}
                />
              </View>
            </View>
          </View>
        </Modal>
      </>
    );
  },
);

const styles = StyleSheet.create({
  beLikeRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.white,
    borderRadius: 18,
    paddingHorizontal: 10,
    paddingVertical: 4,
    elevation: 1,
    marginRight: 4,
  },
  filterContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    borderWidth: 1,
    borderColor: "transparent",
    borderBottomColor: "#ddd",
    padding: 10,
    paddingHorizontal: 16,
  },
  buttonWrapper: {
    flex: 0.4,
    marginRight: 10,
  },
  applyButtonWrapper: {
    flex: 0.6,
    marginRight: 0,
  },

  filterText: {
    marginLeft: 3,
    color: colors.mediumBlack,
    lineHeight: 18,
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: colors.mediumBlacklightBlack,
  },

  chevronIcon: {
    height: 12,
    width: 12,
    marginRight: 10,
    left: -5,
    transform: [{ rotate: "180deg" }],
  },

  modalContainer: {
    backgroundColor: "#fff",
    position: "absolute",
    bottom: 0,
    width: "100%",
    paddingVertical: 20,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },

  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingHorizontal: 20,
  },

  scrollContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
  },

  categoryList: {
    width: "35%",
    backgroundColor: "rgba(245, 245, 245, 1)",
  },

  optionList: {
    width: "65%",
  },

  categoryButton: {
    paddingVertical: 15,
    paddingHorizontal: 10,
  },

  activeCategory: {
    backgroundColor: colors.white,
  },

  activeCategoryText: {
    fontWeight: "bold",
    color: colors.black,
  },

  optionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 15,
    paddingHorizontal: 16,
  },

  activeOption: {
    backgroundColor: "rgba(245, 245, 245, 1)",
  },

  activeOptionText: {
    fontWeight: "bold",
    color: colors.black,
  },

  applyButtonContainer: {
    paddingHorizontal: 10,
    flexDirection: "row",
    marginVertical: 10,
  },
  clearText: {
    color: "rgba(72, 116, 254, 1)",
    textDecorationLine: "underline",
  },

  datePickerContainer: {
    flex: 2,
  },
  outerCircle: {
    height: 18,
    width: 18,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: "#000",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 20,
  },
  selectedOuterCircle: {
    borderColor: colors.black,
  },
  innerCircle: {
    height: 12,
    width: 12,
    borderRadius: 6,
    backgroundColor: "rgba(83, 83, 83, 1)",
  },
  subOptionList: {
    marginLeft: 24,
    // width: "80%",
  },
});

export default WalletFilters;
